import React, { useEffect, useState } from 'react';
import {
   Box,
   Container,
   Typography,
   Paper,
   Button,
   CircularProgress,
   Alert,
   Divider,
   Chip,
   Stack,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   TextField,
   IconButton,
   Snackbar,
} from '@mui/material'; 
import {
   CheckCircle as CheckCircleIcon,
   Error as ErrorIcon,
   Photo as AlbumIcon,
   AccessTime as TimeIcon,
   Person as PersonIcon,
   PhotoLibrary as PhotoLibraryIcon,
   Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../firebase/config';

interface AlbumInvitationData {
   id: string;
   type: 'album';
   status: 'pending' | 'accepted' | 'declined' | 'expired';
   inviterId: string;
   inviterName: string;
   email: string;
   role: string;
   albumId: string;
   albumTitle: string;
   customMessage?: string;
   relationship?: {
      type: string;
      label?: string;
      notes?: string;
   };
}

interface AlbumDetails {
   id: string;
   title: string;
   description?: string;
   createdAt: any;
   createdBy: string;
   photoIds: string[];
   memberIds: string[];
   isPublic: boolean;
}

const AcceptAlbumInvitePage: React.FC = () => {
   const [searchParams] = useSearchParams();
   const invitationId = searchParams.get('invitationId');
   const token = searchParams.get('token');
   const userEmail = searchParams.get('user_email');
   const type = searchParams.get('type');

   const { currentUser } = useUser();
   const { refreshInvitationStatus } = useSubscription();
   const navigate = useNavigate();

   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [success, setSuccess] = useState<string | null>(null);
   const [invitation, setInvitation] = useState<AlbumInvitationData | null>(null);
   const [albumDetails, setAlbumDetails] = useState<AlbumDetails | null>(null);
   const [processing, setProcessing] = useState(false);

   // Registration modal state
   const [registrationModalOpen, setRegistrationModalOpen] = useState(false);
   const [email, setEmail] = useState('');
   const [password, setPassword] = useState('');
   const [confirmPassword, setConfirmPassword] = useState('');
   const [name, setName] = useState('');
   const [submitting, setSubmitting] = useState(false);
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [snackbarMessage, setSnackbarMessage] = useState('');
   const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>(
      'success'
   );
   const [pendingAction, setPendingAction] = useState<'accept' | 'decline' | null>(null);

   useEffect(() => {
      if (token) {
         // Token contains all necessary data, invitationId and type are optional
         loadAlbumData();
      } else {
         setError('No invitation token provided');
         setLoading(false);
      }
   }, [token]);

   const loadAlbumData = async () => {
      try {
         setLoading(true);
         setError(null);

         if (!token) {
            setError('No invitation token provided');
            setLoading(false);
            return;
         }

         // Decode the JWT token to get invitation information
         let decodedToken;
         try {
            const base64Payload = token.split('.')[1];
            const payload = JSON.parse(atob(base64Payload));
            decodedToken = payload;
            console.log('Decoded album invitation token:', decodedToken);
         } catch (err) {
            setError('Invalid invitation token');
            setLoading(false);
            return;
         }

         const invitationEmail = decodedToken.receiver_email;
         const albumId = decodedToken.albumId;
         const albumTitle = decodedToken.albumTitle;
         const role = decodedToken.role;
         const tokenInvitationId = decodedToken.invitationId;

         if (!albumId) {
            setError('Invalid invitation data: Album ID is missing');
            setLoading(false);
            return;
         }

         if (!tokenInvitationId) {
            setError('Invalid invitation data: Invitation ID is missing');
            setLoading(false);
            return;
         }

         if (!invitationEmail) {
            setError('Invalid invitation data: Email is missing');
            setLoading(false);
            return;
         }

         // Fetch actual invitation status from backend
         const invitationStatusResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/invitation-status/${tokenInvitationId}`
         );

         if (!invitationStatusResponse.ok) {
            setError('Failed to fetch invitation status. The invitation may no longer be valid.');
            setLoading(false);
            return;
         }

         const statusData = await invitationStatusResponse.json();
         const actualStatus = statusData.status;

         if (!actualStatus) {
            setError('Invalid invitation status received from server');
            setLoading(false);
            return;
         }

         console.log('Fetched invitation status from backend:', actualStatus);

         // Create invitation data from JWT token
         const invitationData: AlbumInvitationData = {
            id: tokenInvitationId,
            type: 'album',
            status: actualStatus,
            inviterId: decodedToken.inviterId,
            inviterName: decodedToken.sender,
            email: invitationEmail,
            role: role,
            albumId: albumId,
            albumTitle: albumTitle,
            customMessage: decodedToken.customMessage,
         };

         setInvitation(invitationData);
         setEmail(invitationEmail); // Set email from invitation

         // Set album details
         const albumDetails: AlbumDetails = {
            id: albumId,
            title: albumTitle,
            description: statusData.invitation?.description,
            createdAt: new Date(),
            createdBy: 'unknown',
            photoIds: [],
            memberIds: [],
            isPublic: false,
         };
         setAlbumDetails(albumDetails);

         setLoading(false);
      } catch (err) {
         console.error('Error loading album invitation:', err);
         setError('Failed to load album invitation details');
      } finally {
         setLoading(false);
      }
   };

   const handleAcceptInvitation = async () => {
      if (!invitation) return;

      setPendingAction('accept');

      // If user is logged in, proceed directly
      if (currentUser?.uid) {
         await processInvitationAction('accept', invitation.email);
         return;
      }

      // Check if user exists by email
      await checkUserAndProceed('accept');
   };

   const handleDeclineInvitation = async () => {
      if (!invitation) return;

      setPendingAction('decline');

      // If user is logged in, proceed directly
      if (currentUser?.uid) {
         await processInvitationAction('decline', invitation.email);
         return;
      }

      // Check if user exists by email
      await checkUserAndProceed('decline');
   };

   const checkUserAndProceed = async (action: 'accept' | 'decline') => {
      if (!invitation) return;

      setProcessing(true);
      try {
         // Check if user exists
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/auth/check-user-exists`,
            {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({ email: invitation.email }),
            }
         );

         const result = await response.json();

         if (result.exists) {
            // User exists, they need to login first
            setSnackbarMessage('Please login to continue');
            setSnackbarSeverity('info');
            setSnackbarOpen(true);
            setEmail(invitation.email);
            setRegistrationModalOpen(true);
         } else {
            // User doesn't exist, show registration modal
            setEmail(invitation.email);
            setRegistrationModalOpen(true);
         }
      } catch (err) {
         console.error('Error checking user:', err);
         setSnackbarMessage('Failed to process request. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setProcessing(false);
      }
   };

   const handleRegistration = async () => {
      if (!email.trim() || !password.trim() || !name.trim()) {
         setSnackbarMessage('Please fill in all required fields');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      if (password !== confirmPassword) {
         setSnackbarMessage('Passwords do not match');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      setSubmitting(true);
      try {
         await processInvitationAction(pendingAction || 'accept', email, password, name);
         setRegistrationModalOpen(false);
      } catch (err) {
         console.error('Error registering:', err);
         setSnackbarMessage('Registration failed. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setSubmitting(false);
      }
   };

   const handleLogin = async () => {
      if (!email.trim() || !password.trim()) {
         setSnackbarMessage('Please enter your email and password');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      setSubmitting(true);
      try {
         // Login user with Firebase
         await signInWithEmailAndPassword(auth, email, password);

         // After successful login, process the invitation
         await processInvitationAction(pendingAction || 'accept', email);
         setRegistrationModalOpen(false);
      } catch (err: any) {
         console.error('Error logging in:', err);
         setSnackbarMessage(err.message || 'Login failed. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setSubmitting(false);
      }
   };

   const processInvitationAction = async (
      action: 'accept' | 'decline',
      userEmail: string,
      userPassword?: string,
      userName?: string
   ) => {
      if (!invitation || !token) return;

      setProcessing(true);
      setError(null);

      try {
         console.log('📤 Sending album invitation action:', {
            hasToken: !!token,
            invitationId: invitationId || 'none',
            email: userEmail,
            action: action,
            hasPassword: !!userPassword,
            hasName: !!userName,
         });

         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/albums/public-accept`, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify({
               token: token,
               invitationId: invitationId || '',
               email: userEmail,
               password: userPassword,
               name: userName,
               action: action,
            }),
         });

         const result = await response.json();
         console.log('📥 Album invitation action response:', result);

         if (result.success) {
            // Update invitation status in local state immediately
            if (invitation) {
               setInvitation({
                  ...invitation,
                  status: action === 'accept' ? 'accepted' : 'declined',
               });
            }

            // If user just registered (has password), sign them in
            if (userPassword) {
               try {
                  await signInWithEmailAndPassword(auth, userEmail, userPassword);
                  // Refresh invitation status to ensure invited flag is loaded
                  await refreshInvitationStatus();
               } catch (loginErr) {
                  console.error('Error signing in after registration:', loginErr);
               }
            }

            setSuccess(result.message || `Album invitation ${action}ed successfully!`);
            setSnackbarMessage(result.message || `Album invitation ${action}ed successfully!`);
            setSnackbarSeverity('success');
            setSnackbarOpen(true);

            setTimeout(() => {
               // Always redirect to dashboard after accepting or declining
               navigate('/dashboard');
            }, 2000);
         } else {
            if (result.requiresRegistration) {
               setEmail(userEmail);
               setRegistrationModalOpen(true);
            } else {
               const errorMessage = result.error || `Failed to ${action} invitation`;
               console.error('Album invitation action failed:', errorMessage);
               setSnackbarMessage(errorMessage);
               setSnackbarSeverity('error');
               setSnackbarOpen(true);
            }
         }
      } catch (err: any) {
         console.error(`Error ${action}ing invitation:`, err);
         const errorMessage = err.message || `Failed to ${action} invitation. Please try again.`;
         setSnackbarMessage(errorMessage);
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setProcessing(false);
      }
   };

   if (loading) {
      return (
         <Container maxWidth='sm'>
            <Box
               sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '60vh',
               }}
            >
               <CircularProgress />
            </Box>
         </Container>
      );
   }

   // Allow unauthenticated access for album invitation acceptance
   // The invitation page will work with or without authentication

   if (error) {
      return (
         <Container maxWidth='sm'>
            <Alert
               severity='error'
               sx={{ mt: 4 }}
               action={
                  <Button color='inherit' size='small' onClick={() => navigate('/dashboard')}>
                     Go Home
                  </Button>
               }
            >
               {error}
            </Alert>
         </Container>
      );
   }

   if (!invitation || !albumDetails) {
      return (
         <Container maxWidth='sm'>
            <Alert
               severity='error'
               sx={{ mt: 4 }}
               action={
                  <Button color='inherit' size='small' onClick={() => navigate('/dashboard')}>
                     Go Home
                  </Button>
               }
            >
               Invitation not found or has been removed
            </Alert>
         </Container>
      );
   }

   return (
      <Container maxWidth='sm'>
         <Box sx={{ mt: 4, mb: 4 }}>
            <Paper sx={{ p: 3 }}>
               {success ? (
                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 3,
                     }}
                  >
                     <CheckCircleIcon color='success' sx={{ fontSize: 60, mb: 2 }} />
                     <Typography variant='h6' gutterBottom>
                        {success}
                     </Typography>
                     <Typography color='text.secondary'>Redirecting...</Typography>
                  </Box>
               ) : (
                  <>
                     <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <AlbumIcon color='primary' sx={{ mr: 1 }} />
                        <Typography variant='h5'>Invitation to join Album</Typography>
                     </Box>

                     <Divider sx={{ my: 2 }} />

                     <Stack spacing={2}>
                        <Typography variant='h6'>{albumDetails.title}</Typography>

                        {albumDetails.description && (
                           <Typography variant='body2' color='text.secondary'>
                              {albumDetails.description}
                           </Typography>
                        )}

                        <Box>
                           <Typography variant='body2' color='text.secondary'>
                              <TimeIcon fontSize='small' sx={{ verticalAlign: 'middle', mr: 1 }} />
                              Created{' '}
                              {albumDetails.createdAt?.toDate?.()?.toLocaleDateString() ||
                                 'Recently'}
                           </Typography>

                           <Typography variant='body2' color='text.secondary'>
                              <PhotoLibraryIcon
                                 fontSize='small'
                                 sx={{ verticalAlign: 'middle', mr: 1 }}
                              />
                              {albumDetails.photoIds.length} photo
                              {albumDetails.photoIds.length !== 1 ? 's' : ''}
                           </Typography>

                           <Typography variant='body2' color='text.secondary'>
                              <PersonIcon
                                 fontSize='small'
                                 sx={{ verticalAlign: 'middle', mr: 1 }}
                              />
                              {albumDetails.memberIds.length} member
                              {albumDetails.memberIds.length !== 1 ? 's' : ''}
                           </Typography>
                        </Box>

                        {invitation.customMessage && (
                           <Box sx={{ my: 2 }}>
                              <Typography variant='body2' color='text.secondary'>
                                 Message from {invitation.inviterName}:
                              </Typography>
                              <Typography
                                 variant='body1'
                                 sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}
                              >
                                 {invitation.customMessage}
                              </Typography>
                           </Box>
                        )}

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                           <Chip
                              label={`Role: ${
                                 (invitation.role || 'viewer').charAt(0).toUpperCase() +
                                 (invitation.role || 'viewer').slice(1)
                              }`}
                              color='primary'
                              variant='outlined'
                           />
                           <Chip
                              label={albumDetails.isPublic ? 'Public Album' : 'Private Album'}
                              color={albumDetails.isPublic ? 'success' : 'default'}
                              variant='outlined'
                           />
                        </Box>

                        {invitation.status === 'expired' ? (
                           <Alert severity='error'>
                              This invitation has expired. Please request a new invitation.
                           </Alert>
                        ) : invitation.status === 'accepted' ? (
                           <Alert severity='success'>
                              You have already accepted this invitation.
                           </Alert>
                        ) : invitation.status === 'declined' ? (
                           <Alert severity='info'>
                              You have declined this invitation. Contact the album owner for a new
                              invitation if needed.
                           </Alert>
                        ) : (
                           <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                              <Button
                                 variant='contained'
                                 color='primary'
                                 fullWidth
                                 onClick={handleAcceptInvitation}
                                 disabled={processing}
                              >
                                 {processing ? 'Accepting...' : 'Accept Invitation'}
                              </Button>
                              <Button
                                 variant='outlined'
                                 color='error'
                                 fullWidth
                                 onClick={handleDeclineInvitation}
                                 disabled={processing}
                              >
                                 {processing ? 'Declining...' : 'Decline'}
                              </Button>
                           </Box>
                        )}
                     </Stack>
                  </>
               )}
            </Paper>
         </Box>

         {/* Registration/Login Modal */}
         <Dialog
            open={registrationModalOpen}
            onClose={() => setRegistrationModalOpen(false)}
            maxWidth='sm'
            fullWidth
         >
            <DialogTitle>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant='h6'>
                     {pendingAction === 'accept' ? 'Accept' : 'Decline'} Invitation
                  </Typography>
                  <IconButton onClick={() => setRegistrationModalOpen(false)}>
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>
            <DialogContent>
               <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                  Enter your details to {pendingAction === 'accept' ? 'accept' : 'decline'} this
                  invitation.
               </Typography>

               <TextField
                  fullWidth
                  label='Full Name'
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  margin='normal'
                  required
               />

               <TextField
                  fullWidth
                  label='Email Address'
                  type='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin='normal'
                  required
                  disabled
               />

               <TextField
                  fullWidth
                  label='Password'
                  type='password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin='normal'
                  required
               />

               <TextField
                  fullWidth
                  label='Confirm Password'
                  type='password'
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin='normal'
                  required
               />
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setRegistrationModalOpen(false)}>Cancel</Button>
               <Button onClick={handleRegistration} variant='contained' disabled={submitting}>
                  {submitting ? <CircularProgress size={24} /> : 'Submit'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* Snackbar */}
         <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         >
            <Alert
               onClose={() => setSnackbarOpen(false)}
               severity={snackbarSeverity}
               sx={{ width: '100%' }}
            >
               {snackbarMessage}
            </Alert>
         </Snackbar>
      </Container>
   );
};

export default AcceptAlbumInvitePage;
